import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import Loading from "../../common/components/loading/loading";
import {
  generateEvenlySpacedTicks,
  getLinearAxisTickCount,
} from "../../common/components/d3/d3ChartTickUtilities";
import {
  appendBandTooltipTargets,
  appendSignedVerticalAreaGradients,
  buildTooltipHtml,
  createTooltipHandlers,
  dashboardSeriesColors,
  formatTooltipCurrency,
  formatTooltipDate,
  formatTooltipNumber,
  getNumericExtentIncludingZero,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const DailyElectricityPricingAndLoadGraph = ({ data, isLoading }) => {
  const svgRef = useRef();
  const tooltipRef = useRef(null);

  const renderChart = useCallback(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    removeTooltip(tooltipRef);

    if (!svg.node()) {
      return;
    }

    const parentContainer = svg.node().parentNode;
    const parentWidth = parentContainer.clientWidth;
    const parentHeight = parentContainer.clientHeight;
    const margin = { top: 30, right: 60, bottom: 40, left: 35 };

    const innerWidth = parentWidth - margin.left - margin.right;
    const innerHeight = parentHeight - margin.top - margin.bottom;

    if (!innerHeight || innerHeight <= 0 || !innerWidth) return;

    const rollupByDay = (rows) => {
      const dailyData = rows.reduce((acc, item) => {
        const date = new Date(item.beginDateTimeMpt).toLocaleDateString("fr-CA", {
          timeZone: "UTC",
        });
        if (!acc[date]) {
          acc[date] = { count: 0, poolPriceSum: 0, albertaInternalLoadSum: 0 };
        }
        acc[date].count++;
        acc[date].poolPriceSum += item.poolPrice;
        acc[date].albertaInternalLoadSum += item.albertaInternalLoad;
        return acc;
      }, {});

      return Object.entries(dailyData).map(([date, values]) => ({
        date: new Date(date),
        poolPrice: values.poolPriceSum / values.count,
        albertaInternalLoad: values.albertaInternalLoadSum / values.count,
      }));
    };

    const formattedData = rollupByDay(data);
    const priceColor = dashboardSeriesColors.accentRed;
    const loadColor = dashboardSeriesColors.accentBlue;

    const chartSvg = svg
      .append("svg")
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
      .attr("preserveAspectRatio", "none")
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const xScale = d3
      .scaleBand()
      .domain(formattedData.map((d) => d.date))
      .range([0, innerWidth])
      .padding(0.3);

    const yScale = d3
      .scaleLinear()
      .domain(getNumericExtentIncludingZero(formattedData.map((d) => d.poolPrice)))
      .nice()
      .range([innerHeight, 0]);

    const yScaleSecondary = d3
      .scaleLinear()
      .domain([
        d3.min(formattedData, (d) => d.albertaInternalLoad),
        d3.max(formattedData, (d) => d.albertaInternalLoad),
      ])
      .nice()
      .range([innerHeight, 0]);

    chartSvg
      .append("g")
      .attr("class", "grid d3-chart-gridline")
      .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

    const dateArray = formattedData.map((d) => d.date);
    const maxXTicks = getLinearAxisTickCount(innerWidth, 80);
    const xTickValues = generateEvenlySpacedTicks(dateArray, maxXTicks);

    chartSvg
      .append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickValues(xTickValues.length > 0 ? xTickValues : undefined)
          .ticks(xTickValues.length > 0 ? undefined : maxXTicks)
          .tickFormat(d3.timeFormat("%b %d")),
      )
      .attr("class", "d3-chart-axis")
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em");

    const formatCurrency = d3.format("$,.0f");
    const maxYTicks = getLinearAxisTickCount(innerHeight, 40);
    chartSvg
      .append("g")
      .call(d3.axisLeft(yScale).ticks(maxYTicks).tickFormat(formatCurrency))
      .attr("class", "d3-chart-axis");

    chartSvg
      .append("g")
      .attr("transform", `translate(${innerWidth},0)`)
      .call(d3.axisRight(yScaleSecondary).ticks(maxYTicks))
      .attr("class", "d3-chart-axis");

    chartSvg
      .append("text")
      .attr("transform", "rotate(90)")
      .attr("x", innerHeight / 2)
      .attr("y", -(innerWidth + margin.right - 10))
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .attr("class", "d3-chart-axis-label")
      .text("Load (MW)");

    chartSvg
      .append("g")
      .selectAll(".grid")
      .data(yScale.ticks())
      .enter()
      .append("line")
      .attr("class", "grid d3-chart-gridline")
      .attr("x1", 0)
      .attr("x2", innerWidth)
      .attr("y1", (d) => yScale(d))
      .attr("y2", (d) => yScale(d));

    chartSvg
      .selectAll(".bar.albertaInternalLoad")
      .data(formattedData)
      .enter()
      .append("rect")
      .attr("class", "bar dashboard-bar albertaInternalLoad")
      .attr("x", (d) => xScale(d.date))
      .attr("y", (d) => yScaleSecondary(d.albertaInternalLoad))
      .attr("width", xScale.bandwidth())
      .attr("rx", 3)
      .attr("ry", 3)
      .attr("height", (d) => innerHeight - yScaleSecondary(d.albertaInternalLoad))
      .attr("fill", loadColor);

    const gradients = appendSignedVerticalAreaGradients({
      chartSvg,
      idPrefix: "daily-price-load-price",
      color: priceColor,
    });

    const priceAreaPositive = d3
      .area()
      .defined((d) => Number.isFinite(d.poolPrice) && d.poolPrice >= 0)
      .x((d) => xScale(d.date) + xScale.bandwidth() / 2)
      .y0(() => yScale(0))
      .y1((d) => yScale(Math.max(d.poolPrice, 0)))
      .curve(d3.curveMonotoneX);

    const priceAreaNegative = d3
      .area()
      .defined((d) => Number.isFinite(d.poolPrice) && d.poolPrice <= 0)
      .x((d) => xScale(d.date) + xScale.bandwidth() / 2)
      .y0(() => yScale(0))
      .y1((d) => yScale(Math.min(d.poolPrice, 0)))
      .curve(d3.curveMonotoneX);

    const linePoolPrice = d3
      .line()
      .defined((d) => Number.isFinite(d.poolPrice))
      .x((d) => xScale(d.date) + xScale.bandwidth() / 2)
      .y((d) => yScale(d.poolPrice))
      .curve(d3.curveMonotoneX);

    const areaLayer = chartSvg.append("g").attr("class", "dashboard-area-layer");
    areaLayer
      .append("path")
      .datum(formattedData)
      .attr("class", "d3-chart-area dashboard-area-highlight")
      .attr("fill", `url(#${gradients.negativeGradientId})`)
      .attr("d", priceAreaNegative);
    areaLayer
      .append("path")
      .datum(formattedData)
      .attr("class", "d3-chart-area dashboard-area-highlight")
      .attr("fill", `url(#${gradients.positiveGradientId})`)
      .attr("d", priceAreaPositive);

    chartSvg
      .append("path")
      .datum(formattedData)
      .attr("fill", "none")
      .attr("stroke", priceColor)
      .attr("stroke-width", 2)
      .attr("d", linePoolPrice);

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });

    appendBandTooltipTargets({
      chartSvg,
      data: formattedData,
      xScale,
      innerHeight,
      xAccessor: (d) => d.date,
      showTooltip,
      hideTooltip,
      htmlAccessor: (d) =>
        buildTooltipHtml({
          title: formatTooltipDate(d.date, "%Y-%m-%d"),
          rows: [
            {
              label: "Price",
              value: formatTooltipCurrency(d.poolPrice, 0),
              color: priceColor,
            },
            {
              label: "Load",
              value: `${formatTooltipNumber(d.albertaInternalLoad, {
                maximumFractionDigits: 0,
              })} MW`,
              color: loadColor,
            },
          ],
        }),
    });

    const legend = chartSvg
      .append("g")
      .attr("transform", `translate(${innerWidth / 2}, -10)`);

    chartSvg
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text("AESO Average 7X24 Price and Load")
      .attr("class", "d3-chart-title");

    legend
      .append("line")
      .attr("x1", innerWidth / 4 - 60)
      .attr("y1", 5)
      .attr("x2", innerWidth / 4 - 30)
      .attr("y2", 5)
      .attr("stroke", priceColor)
      .attr("stroke-width", 2);

    legend
      .append("text")
      .attr("x", innerWidth / 4 - 25)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Price");

    legend
      .append("line")
      .attr("x1", innerWidth / 4 + 30)
      .attr("y1", 5)
      .attr("x2", innerWidth / 4 + 60)
      .attr("y2", 5)
      .attr("stroke", loadColor)
      .attr("stroke-width", 2);

    legend
      .append("text")
      .attr("x", innerWidth / 4 + 65)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Load");
  }, [data]);

  useEffect(() => {
    !isLoading && renderChart();

    return () => {
      removeTooltip(tooltipRef);
    };
  }, [renderChart, isLoading]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current;
    if (!svgNode) return;

    const throttledResize = () => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100);
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (svgNode) {
      observer.observe(svgNode.parentNode);
    }

    return () => {
      if (svgNode && svgNode.parentNode) {
        observer.unobserve(svgNode.parentNode);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading]);

  if (isLoading ?? true) return <Loading />;
  return <svg ref={svgRef} className="dashboard-d3-chart" width="100%" height="100%"></svg>;
};

export default DailyElectricityPricingAndLoadGraph;
